import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { WindowRefService } from '@callrail/looky/util';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { fsSession } from '../../constants/full-story-session';
import { AppContextService } from '../app-context/app-context.service';
import { CurrentContextService } from '../current-context/current-context.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorHelperService {
  constructor(
    private context: CurrentContextService,
    private appContext: AppContextService,
    private http: HttpClient,
    private windowRefService: WindowRefService,
    @Optional() @Inject('env') private env?: any // looky apps
  ) {}

  // Stack-frame origins that only ever come from browser extensions or other injected scripts,
  // never from our own bundles. `blob:` is how some crypto-wallet extensions (e.g. Rainbow,
  // chrome-extension://opfgelmcmbiajamepnmloijbpoleiama) load their injected provider code, which
  // hides the extension id that a raw chrome-extension:// frame would otherwise expose.
  private static readonly INJECTED_ORIGIN_RE =
    /(?:blob:|chrome-extension:\/\/|moz-extension:\/\/|safari-extension:\/\/|safari-web-extension:\/\/)/;

  // Frames that point back at our own shipped code. If any frame is ours, we still want the notice.
  private static readonly OWN_FRAME_RE = /\/packs\/|assets\.calltrk\.com/;

  // Intercepted HTTP statuses that are too noisy or not actionable from the front end to report:
  // 401 expired/again-and-again Devise sessions, 403 inactive accounts / API rate limits,
  // 404 intentional (Hackerone brute-force mitigation), 408 pg statement timeouts, 500/502 handled
  // server-side, 504 user-specific timeouts. All better understood in HB/Datadog on the backend.
  private static readonly NON_ACTIONABLE_STATUSES = [
    401, 403, 404, 408, 500, 502, 504,
  ];

  // Error messages describing browser/system conditions outside our control: failed chunk loads on
  // poor connections, microphone/camera permission or availability, and audio decode failures.
  private static readonly NON_ACTIONABLE_MESSAGE_SNIPPETS = [
    'ChunkLoadError',
    'SecurityError: No permission to use requested device',
    'NotAllowedError: Permission denied',
    'NotFoundError: Requested device not found',
    'EncodingError: Decoding failed',
  ];

  public doNotify(error: any): boolean {
    try {
      return !this.isNonActionable(error);
    } catch (err) {
      return false;
    }
  }

  // Reasons we deliberately do NOT report an error to Honeybadger, as a list of independent
  // predicates. `some` short-circuits, preserving the original top-to-bottom evaluation order
  // without a deep boolean expression or a long chain of early returns.
  private isNonActionable(error: any): boolean {
    if (this.isInjectedScriptNoise(error)) {
      // Errors thrown entirely inside browser-extension / injected scripts (crypto-wallet
      // providers, etc.) surface with blob:/chrome-extension:// stack frames and never touch looky
      // code (empty application_trace). We can't fix a customer's own extension, and each blob: URL
      // gets a fresh per-page-load UUID, so these fingerprint into an endless stream of
      // single-notice faults. See CR-60006.
      return true;
    }

    const { name, message, status } = error;

    const reasons: (() => unknown)[] = [
      // window.onerror CORS "Script error" / ResizeObserver-loop noise
      () => this.isWindowOnerrorNoise(name, message),
      // TestBox injection errors
      () => name === 'TypeError: handler is not a function',
      // intercepted HTTP statuses that are too noisy / better tracked server-side
      () => this.isNonActionableHttpError(name, message, status),
      // meshimer http-intercepting service swaps our response for an "ACCESS RESTRICTED" HTML
      // blurb; only solvable by the customer allow-listing our domain
      () => error?.error?.text?.match(/meshimer/i),
      () => error?.error?.message?.includes('ChunkLoadError'),
      // browser/system conditions outside our control (device perms, chunk loads, audio decode)
      () =>
        ErrorHelperService.NON_ACTIONABLE_MESSAGE_SNIPPETS.some((snippet) =>
          message?.includes(snippet)
        ),
    ];

    return reasons.some((reason) => Boolean(reason()));
  }

  private isWindowOnerrorNoise(name: any, message: any): boolean {
    if (name !== 'window.onerror') {
      return false;
    }
    // CORS issue https://app.honeybadger.io/projects/56607/faults/38611350
    const isCorsScriptError =
      (typeof message === 'string' &&
        message.match(/["']isTrusted["']=>["']true["']/)) ||
      (typeof message === 'object' && message.message?.match(/Script error/));
    // ResizeObserver loop, caused by password autofill extensions
    const isResizeObserverLoop = message?.startsWith?.(
      'ResizeObserver loop completed'
    );
    return Boolean(isCorsScriptError || isResizeObserverLoop);
  }

  private isNonActionableHttpError(
    name: any,
    message: any,
    status: any
  ): boolean {
    // status 0 is the pre-completion code XHR errors surface (CORS, conn refused, timeouts, cert
    // issues); not actionable unless it were affecting everyone
    if (status === 0 && name && name.match(/HttpErrorResponse/)) {
      return true;
    }
    // ...the same error delivered as stringified JSON in the message body
    if (
      message?.includes('"status":0,') &&
      message?.includes('"name":"HttpErrorResponse"')
    ) {
      return true;
    }
    // statuses too noisy / better tracked server-side, as a real status or embedded in the message
    return (
      ErrorHelperService.NON_ACTIONABLE_STATUSES.includes(status) ||
      ErrorHelperService.NON_ACTIONABLE_STATUSES.some((code) =>
        message?.includes(`"status":${code},`)
      )
    );
  }

  public metaData(error, additionalMetaData?) {
    const context = this.hbContext(error, additionalMetaData);
    const metaData = { context } as any;
    const fingerprint = this.fingerprint(error);
    if (fingerprint) {
      metaData.fingerprint = fingerprint;
    }
    return metaData;
  }

  public helpfulNamedError(error) {
    // Some errors get grouped together and we need to differentiate them in Honeybadger better for grouping and scannability
    switch (error?.name) {
      case 'HttpErrorResponse':
        const url = error.url || this.xhrURL(error) || '';
        error.name =
          error.name + '::' + error.status + '::' + this.endpoint(url); // HttpErrorResponse::500::calls.json
        break;
      case 'TypeError':
        if (error.message || error.message.message) {
          error.name =
            error.name +
            ': ' +
            (error.message ? error.message : error.message.message).substring(
              0,
              80
            );
        }
        break;
    }

    return error;
  }

  public get isMySpaFresh(): Observable<boolean> {
    if (
      !this.appContext.isLookyApp ||
      this.env.staging ||
      this.env.development
    ) {
      return of(true);
    }
    let app = this.appContext.currentAppName; // cfb, lead-center, superadmin, analytics, account-center
    if (app === 'sa') {
      app = 'superadmin';
    }
    const url = `https://calltrk-build-artifacts-prod1.s3.amazonaws.com/looky/${app}/main/latest/sha`;
    return this.http.get(url, { responseType: 'text' }).pipe(
      map((lastestSha) => lastestSha === process.env.REVISION),
      catchError(() => of(true)) // if we cant get the latest sha, assume its fresh
    );
  }

  private isInjectedScriptNoise(error: any): boolean {
    const stack = typeof error?.stack === 'string' ? error.stack : '';
    if (!stack) {
      return false;
    }
    const hasInjectedFrame = ErrorHelperService.INJECTED_ORIGIN_RE.test(stack);
    const hasOwnFrame = ErrorHelperService.OWN_FRAME_RE.test(stack);
    // Only drop it when the error is entirely third-party: at least one injected frame and none
    // of our own. A stack that mixes an extension frame with a looky frame is still reported.
    return hasInjectedFrame && !hasOwnFrame;
  }

  private hbContext(error, additionalMetaData) {
    return Object.assign(
      {
        user: this.context.currentUser ? this.context.currentUser.id : null,
        role: this.context.currentUser ? this.context.currentUser.role : null,
        white_label: this.context.currentUser
          ? this.context.currentUser.white_label
          : null,
        agency: this.context.currentAgency
          ? this.context.currentAgency.id
          : null,
        company: this.context.currentCompany
          ? this.context.currentCompany.id
          : null,
        xhr_url: this.xhrURL(error),
        fs_session: fsSession(true),
        error: {
          error: error.error,
          errors: error.errors,
          message: error.message,
          name: error.message,
          ok: error.ok,
          status: error.status,
          statusText: error.statusText,
          url: error.url,
        },
        tags: this.tags,
        component: this.getModuleFromError(error),
      },
      additionalMetaData || {}
    );
  }

  private fingerprint(error): string {
    // poor connections plus large chunk files cause this error to constantly happen and every
    // one of them are grouped different based upon which chunk file + hashing it was for. So group them
    if (
      typeof error.message === 'string' &&
      error.message.match(/[Ll]oading chunk.*failed.*timeout/)
    ) {
      return 'timeout loading chunk';
    } else if (error.name.match(/HttpErrorResponse/)) {
      return error.name; // HttpErrorResponse::500::calls.json
    } else if (
      typeof error.message === 'string' &&
      error.message.match(/Invalid semrush destination/)
    ) {
      return error.message.replace(/\s/g, '-');
    }
  }

  private endpoint(url: string): string {
    for (let part of url.split('/').reverse()) {
      part = part.replace(/[#|\?|&].*/, ''); // remove query string
      if (part.match(/^[a-z|A-Z]/)) {
        return part;
      } // starts with a letter
    }
  }

  private xhrURL(error): string {
    if (error && error.error && error.error.target) {
      return error.error.target.__zone_symbol__xhrURL; // "/a/243316838/current_plan.json"
    }
  }

  private get tags(): string {
    const app = this.appContext.currentAppName;
    const list = [app];
    if (this.windowRefService.inSemrushIframe()) {
      list.push('semrush');
    }
    return list.join(', ');
  }

  private getModuleFromError(error): string {
    const { stack } = error;
    if (!stack) {
      return '';
    }
    const lines = stack.split('\n');
    const firstStackLine = lines.find((line) => line.trim().startsWith('at'));
    if (!firstStackLine) {
      return '';
    }
    const moduleRegex = new RegExp(/\/packs\/([a-zA-Z\-]+)\/([0-9]+)[.\-]/);
    const packRegex = new RegExp(/\/packs\/(.*)\//);
    const moduleMatch = firstStackLine.match(moduleRegex);
    const packMatch = firstStackLine.match(packRegex);
    if (moduleMatch) {
      return `${moduleMatch[1]}-module-${moduleMatch[2]}`;
    } else if (packMatch) {
      return packMatch[1];
    } else {
      return '';
    }
  }
}
